import React, { useEffect, useMemo, useState } from 'react'
import { supabase } from '@/lib/supabaseClient'

const COLUMNS = [
  'created_at',
  'status',
  'contact_name',
  'contact_email',
  'contact_organisation',
  'title',
  'slug',
  'organiser',
  'type',
  'date',
  'start_datetime',
  'end_datetime',
  'venue_name',
  'street_name',
  'street_number',
  'postal_code',
  'city',
  'region',
  'description',
  'ticket_text',
  'ticket_url',
  'image_url',
  'terms',
  'time_zone',
  'sanity_draft_id',
  'sanity_error',
  'synced_to_sanity_at',
  'funded_by_herdenkingscomite',
]

function formatColumnLabel(key) {
  return key.replaceAll('_', ' ')
}

function formatCellValue(value, key) {
  if (value === null || value === undefined || value === '') return '—'

  if (typeof value === 'boolean') {
    return value ? 'Ja' : 'Nee'
  }

  if (
    key.includes('created_at') ||
    key.includes('datetime') ||
    key.includes('synced_to_sanity_at')
  ) {
    const date = new Date(value)

    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleString('nl-NL', {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    }
  }

  return String(value)
}

function AdminLogin({ email, setEmail, onSubmit, loading, message, error }) {
  return (
    <main className="min-h-screen bg-background px-4 py-20 text-[#1f1a17]">
      <section className="mx-auto max-w-md rounded-3xl border border-black/10 bg-white p-8 shadow-sm">
        <p className="mb-3 text-xs uppercase tracking-[0.2em] text-black/50">
          Beheer
        </p>

        <h1 className="mb-3 text-3xl font-medium tracking-tight">
          Admin login
        </h1>

        <p className="mb-8 text-sm leading-6 text-black/60">
          Log in met je e-mailadres. Je ontvangt een beveiligde magic link.
        </p>

        <form onSubmit={onSubmit} className="space-y-4">
          <label className="block">
            <span className="mb-2 block text-sm font-medium">
              E-mailadres
            </span>

            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="naam@organisatie.nl"
              className="w-full rounded-xl border border-black/15 bg-white px-4 py-3 text-base outline-none transition focus:border-black"
            />
          </label>

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-xl bg-[#2b1b55] px-4 py-3 text-sm font-medium text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? 'Link wordt verstuurd…' : 'Stuur login link'}
          </button>
        </form>

        {message && (
          <p className="mt-5 rounded-xl bg-green-50 px-4 py-3 text-sm text-green-800">
            {message}
          </p>
        )}

        {error && (
          <p className="mt-5 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
          </p>
        )}
      </section>
    </main>
  )
}

export default function AdminPage() {
  const [session, setSession] = useState(null)
  const [checkingSession, setCheckingSession] = useState(true)
  const [checkingAdmin, setCheckingAdmin] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)

  const [email, setEmail] = useState('')
  const [loginLoading, setLoginLoading] = useState(false)
  const [loginMessage, setLoginMessage] = useState('')
  const [loginError, setLoginError] = useState('')

  const [rows, setRows] = useState([])
  const [rowsLoading, setRowsLoading] = useState(false)
  const [rowsError, setRowsError] = useState('')
  const [deletingId, setDeletingId] = useState(null)
  const [search, setSearch] = useState('')

  useEffect(() => {
    let mounted = true

    async function initSession() {
      const { data, error } = await supabase.auth.getSession()

      if (!mounted) return

      if (error) {
        console.error(error)
      }

      setSession(data?.session || null)
      setCheckingSession(false)
    }

    initSession()

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession)
    })

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (!session) {
      setIsAdmin(false)
      setRows([])
      return
    }

    checkAdminAndLoadRows()
  }, [session])

  async function checkAdminAndLoadRows() {
    setCheckingAdmin(true)
    setRowsError('')

    const { data, error } = await supabase.rpc('is_admin')

    if (error) {
      console.error(error)
      setRowsError('Kon adminrechten niet controleren.')
      setIsAdmin(false)
      setCheckingAdmin(false)
      return
    }

    if (!data) {
      setIsAdmin(false)
      setCheckingAdmin(false)
      return
    }

    setIsAdmin(true)
    setCheckingAdmin(false)

    await loadRows()
  }

  async function loadRows() {
    setRowsLoading(true)
    setRowsError('')

    const { data, error } = await supabase
      .from('event_submissions')
      .select('*')
      .order('created_at', { ascending: false })

    if (error) {
      console.error(error)
      setRowsError('Kon inzendingen niet laden.')
      setRows([])
      setRowsLoading(false)
      return
    }

    setRows(data || [])
    setRowsLoading(false)
  }

  async function handleLogin(event) {
    event.preventDefault()

    setLoginLoading(true)
    setLoginMessage('')
    setLoginError('')

    const redirectTo = `${window.location.origin}/admin`

    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: redirectTo,
        shouldCreateUser: false,
      },
    })

    if (error) {
      console.error(error)
      setLoginError(
        'Login link kon niet worden verstuurd. Controleer of dit e-mailadres als Auth user bestaat.'
      )
      setLoginLoading(false)
      return
    }

    setLoginMessage('Check je inbox voor de login link.')
    setLoginLoading(false)
  }

  async function handleSignOut() {
    await supabase.auth.signOut()
    setRows([])
    setIsAdmin(false)
  }

  async function handleDelete(row) {
    const confirmed = window.confirm(
      `Weet je zeker dat je deze inzending permanent wilt verwijderen?\n\n${row.title || row.contact_email || row.id}`
    )

    if (!confirmed) return

    setDeletingId(row.id)
    setRowsError('')

    const { error } = await supabase
      .from('event_submissions')
      .delete()
      .eq('id', row.id)

    if (error) {
      console.error(error)
      setRowsError('Verwijderen is mislukt.')
      setDeletingId(null)
      return
    }

    setRows((currentRows) =>
      currentRows.filter((currentRow) => currentRow.id !== row.id)
    )

    setDeletingId(null)
  }

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase()

    if (!query) return rows

    return rows.filter((row) => {
      return COLUMNS.some((column) => {
        const value = row[column]
        return String(value || '').toLowerCase().includes(query)
      })
    })
  }, [rows, search])

  if (checkingSession) {
    return (
      <main className="min-h-screen bg-[#f7f3ed] px-4 py-20 text-[#1f1a17]">
        <div className="mx-auto max-w-6xl">Sessie controleren…</div>
      </main>
    )
  }

  if (!session) {
    return (
      <AdminLogin
        email={email}
        setEmail={setEmail}
        onSubmit={handleLogin}
        loading={loginLoading}
        message={loginMessage}
        error={loginError}
      />
    )
  }

  if (checkingAdmin) {
    return (
      <main className="min-h-screen bg-[#f7f3ed] px-4 py-20 text-[#1f1a17]">
        <div className="mx-auto max-w-6xl">Adminrechten controleren…</div>
      </main>
    )
  }

  if (!isAdmin) {
    return (
      <main className="min-h-screen bg-[#f7f3ed] px-4 py-20 text-[#1f1a17]">
        <section className="mx-auto max-w-md rounded-3xl border border-black/10 bg-white p-8 shadow-sm">
          <h1 className="mb-3 text-2xl font-medium">Geen toegang</h1>

          <p className="mb-6 text-sm leading-6 text-black/60">
            Je bent ingelogd, maar dit account staat niet in de adminlijst.
          </p>

          <button
            type="button"
            onClick={handleSignOut}
            className="rounded-xl bg-[#2b1b55] px-4 py-3 text-sm font-medium text-white"
          >
            Uitloggen
          </button>
        </section>
      </main>
    )
  }

  return (
    <main className="min-h-screen bg-background px-4 py-10 text-black">
      <section className="mx-auto max-w-[1600px]">
        <header className="mb-8 flex flex-col gap-5 border-b border-black/10 pb-6 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="mb-2 text-xs uppercase tracking-[0.2em] text-[#2b1b55]">
              Beheer
            </p>

            <h1 className="text-4xl font-medium tracking-tight text-[#5757fd]">
              Inzendingen
            </h1>

            <p className="mt-3 text-sm text-[#2b1b55]">
              Alleen bekijken en permanent verwijderen. Bewerken is uitgeschakeld.
            </p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={loadRows}
              disabled={rowsLoading}
              className="rounded-xl border border-white hover:border-white text-[#2b1b55] bg-white px-4 py-3 text-sm font-medium transition hover:bg-background disabled:opacity-50"
            >
              {rowsLoading ? 'Laden…' : 'Vernieuwen'}
            </button>

            <button
              type="button"
              onClick={handleSignOut}
              className="rounded-xl bg-[#2b1b55] px-4 py-3 text-sm font-medium text-white transition hover:opacity-90"
            >
              Uitloggen
            </button>
          </div>
        </header>

        <div className="mb-5 grid gap-3 md:grid-cols-[1fr_auto] md:items-center">
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Zoeken op naam, e-mail, organisatie, titel, stad…"
            className="w-full rounded-xl border border-black/15 bg-white px-4 py-3 text-sm outline-none transition focus:border-black"
          />

          <p className="text-sm text-black/60">
            {filteredRows.length} van {rows.length} inzendingen
          </p>
        </div>

        {rowsError && (
          <p className="mb-5 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-800">
            {rowsError}
          </p>
        )}

        <div className="overflow-hidden rounded-3xl border border-black/10 bg-white shadow-sm">
          <div className="max-h-[72vh] overflow-auto">
            <table className="min-w-[2200px] border-collapse text-left text-sm">
              <thead className="sticky top-0 z-10 bg-[#2b1b55] text-white">
                <tr>
                  <th className="sticky left-0 z-20 border-b border-black/10 bg-[#2b1b55] text-white px-4 py-3 font-medium">
                    Actie
                  </th>

                  {COLUMNS.map((column) => (
                    <th
                      key={column}
                      className="whitespace-nowrap border-b border-black/10 px-4 py-3 font-medium"
                    >
                      {formatColumnLabel(column)}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {rowsLoading && (
                  <tr>
                    <td
                      colSpan={COLUMNS.length + 1}
                      className="px-4 py-8 text-center text-black/50"
                    >
                      Inzendingen laden…
                    </td>
                  </tr>
                )}

                {!rowsLoading && filteredRows.length === 0 && (
                  <tr>
                    <td
                      colSpan={COLUMNS.length + 1}
                      className="px-4 py-8 text-center text-black/50"
                    >
                      Geen inzendingen gevonden.
                    </td>
                  </tr>
                )}

                {!rowsLoading &&
                  filteredRows.map((row) => (
                    <tr
                      key={row.id}
                      className="border-b border-black/5 align-top last:border-b-0 hover:bg-black/[0.02]"
                    >
                      <td className="sticky left-0 z-10 bg-white px-4 py-3">
                        <button
                          type="button"
                          onClick={() => handleDelete(row)}
                          disabled={deletingId === row.id}
                          className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {deletingId === row.id
                            ? 'Verwijderen…'
                            : 'Permanent verwijderen'}
                        </button>
                      </td>

                      {COLUMNS.map((column) => {
                        const value = row[column]
                        const formattedValue = formatCellValue(value, column)

                        if (
                          (column === 'ticket_url' || column === 'image_url') &&
                          value
                        ) {
                          return (
                            <td
                              key={column}
                              className="max-w-[320px] border-l border-black/5 px-4 py-3"
                            >
                              <a
                                href={value}
                                target="_blank"
                                rel="noreferrer"
                                className="break-all text-[#2b1b55] underline underline-offset-2"
                              >
                                {formattedValue}
                              </a>
                            </td>
                          )
                        }

                        return (
                          <td
                            key={column}
                            className="max-w-[420px] border-l border-black/5 px-4 py-3"
                          >
                            <span className="line-clamp-6 whitespace-pre-wrap break-words">
                              {formattedValue}
                            </span>
                          </td>
                        )
                      })}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </main>
  )
}